import React, { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useTranslate, useShallowEqualSelector } from '@hooks';
import { useForm } from '@dotone/react-ui-core/hooks';
import { createSearchKey, dismissCreateSearchKey } from '@actions';
import { camelCase, snakeCase, trim } from 'lodash';
import { formatDateRange, periodToDateRange } from '@dotone/react-ui-core/utils';
import { Form } from 'reactstrap';
import {
  FormGroup,
  FormAction,
  Label,
  Input,
  Button,
  ButtonCancel,
  StatSearchFieldSelect,
} from '@dotone/react-ui-core';

const StatSearchForm = ({
  defaultValues: givenDefaultValues = {},
  onSubmit,
  onDismiss,
  ...otherProps
}) => {
  const { t } = useTranslate('stat', 'search');
  const router = useRouter();
  const dispatch = useDispatch();
  let { startDate, endDate } = router.query;

  const {
    data: { searchKey },
    isLoading: submitting,
    status,
  } = useShallowEqualSelector(({ searchKeyCreate }) => searchKeyCreate);

  const { startDate: defaultStartDate, endDate: defaultEndDate } = formatDateRange();
  startDate = startDate || defaultStartDate;
  endDate = endDate || defaultEndDate;

  const { field, search = [] } = givenDefaultValues;
  const defaultValues = {
    ...givenDefaultValues,
    field: field ? camelCase(field) : null,
    search: search.join('\n'),
  };

  const { handleSubmit, connectForm, reset, watch } = useForm({ defaultValues });
  const searchField = watch('field');

  const handleSearchKeyChange = useCallback(
    (value) => {
      const dateRange =
        value && ['id', 'orderNumber', 'partialId', 'partialOrderNumber'].includes(searchField)
          ? periodToDateRange(new Date(), 'statLimit')
          : { startDate, endDate };

      onSubmit({ searchKey: value }, dateRange);
    },
    [startDate, endDate, searchField, onSubmit]
  );

  const handleReset = () => {
    handleSearchKeyChange(null);
    reset();
  };

  const handleSubmitForm = (values) => {
    const data = {
      ...values,
      search: values.search.split('\n').map(trim),
      field: snakeCase(values.field),
    };

    dispatch(createSearchKey(data));
  };

  useEffect(() => {
    if (searchKey && status === 'success' && !submitting) {
      dispatch(dismissCreateSearchKey());
      handleSearchKeyChange(searchKey);
    }
  }, [dispatch, searchKey, status, submitting, handleSearchKeyChange]);

  return (
    <Form onSubmit={handleSubmit(handleSubmitForm)} {...otherProps}>
      {connectForm(
        <>
          <FormGroup>
            <Label
              description={
                searchField &&
                !['id', 'orderNumber', 'partialId', 'partialOrderNumber'].includes(searchField) &&
                t('stat:searchForm.description', {
                  startDate,
                  endDate,
                })
              }
            >
              {t('searchBy')}
            </Label>
            <StatSearchFieldSelect name="field" rules={{ required: true }} />
          </FormGroup>
          <FormGroup>
            <Label>{t('search')}</Label>
            <Input type="textarea" name="search" rules={{ required: true }} rows={5} />
          </FormGroup>
        </>
      )}
      <FormAction align="right">
        <Button type="submit" color="primary" submitting={submitting}>
          {t('common:actions.submit')}
        </Button>
        <Button color="secondary" onClick={handleReset}>
          {t('common:actions.reset')}
        </Button>
        <ButtonCancel disabled={submitting} onClick={onDismiss} />
      </FormAction>
    </Form>
  );
};

export default StatSearchForm;
